// Контракт кнопок блока (gamified-info-block-buttons.md). Всё, что студия знает про
// кнопки, живёт здесь: отдельных ручек add/update/delete нет — список целиком уезжает
// в PUT блока, поэтому важно, чтобы форма писала ровно то, что бек ждёт.
import type { BlockButton, BlockType } from '@/types/blocks'
import type { ContentLang } from '../editorSlice'

/** Единственный тип, который создаёт студия (§2). `type` — свободная строка, не enum:
 *  бек её не читает, а NEXT_BUTTON/RETRY из других сценариев мы не показываем. */
const LINK_BUTTON_TYPE = 'LINK'

/** Как читается кнопка, которой автор не дал названия — и в форме, и в превью. */
export const DEFAULT_BUTTON_TITLE = 'Кнопка'

export const BUTTON_LIMITS = { COUNT: 3, TITLE: 255, LINK: 1000 } as const

/** Сколько кнопок можно завести НА ОДИН ЯЗЫК у блока этого типа. У блока картинки
 *  (`info`) заказчик оставил одну (бэк-лог, п.20b), у остальных — общий потолок.
 *  Единственный источник порога: его читают и форма, и всё, что решает, можно ли
 *  добавить ещё одну. Второй копии не заводить. */
export const buttonLimitFor = (type: BlockType): number =>
  type === 'info' ? 1 : BUTTON_LIMITS.COUNT

/** Язык кнопки. `lang` — свободная строка (§2), и в системе встречается верхний
 *  регистр («EN» у academy-frontend), поэтому сравниваем без учёта регистра: иначе
 *  английская кнопка из старых данных молча попадала бы в русский список.
 *  Пустой `lang` (кнопки не из студии) считаем базовым — иначе они пропали бы из
 *  формы совсем и уехали бы на бек нетронутыми. */
const langOf = (button: BlockButton): ContentLang =>
  button.lang?.trim().toLowerCase() === 'en' ? 'en' : 'ru'

/** На запись отдаём ВЕРХНИЙ регистр — как договорились с плеером, а НЕ потому,
 *  что нижний там не сработает: живой плеер (academy-frontend,
 *  `.../NextBlockButton/blockButtons.ts`) сравнивает язык через
 *  `lang?.trim().toLowerCase() === 'en'`, то есть регистр ему безразличен.
 *  Само поле — свободная строка, бек его не читает. */
const writeLang = (button: BlockButton): string => langOf(button).toUpperCase()

/** Пустое значение — всегда `null`, а не `''`: на публикации бек одинаково ругается на
 *  оба (§7.2), но хранить два способа сказать «пусто» в одном поле незачем. */
const trimmed = (value: string | null): string | null => value?.trim() || null

export const newLinkButton = (lang: ContentLang): BlockButton => ({
  type: LINK_BUTTON_TYPE,
  lang: lang.toUpperCase(),
  title: DEFAULT_BUTTON_TITLE,
  link: null,
  slug: null,
  color: null,
  order: 0,
})

export const buttonsForLang = (
  buttons: BlockButton[] | undefined,
  lang: ContentLang,
): BlockButton[] => (buttons ?? []).filter((b) => langOf(b) === lang)

/** Правки текущего языка + нетронутый чужой язык. Базовый язык идёт первым, чтобы
 *  глобальный `order` (см. normalizeButtons) не перемешивал языки между собой. */
export const mergeButtonsForLang = (
  buttons: BlockButton[] | undefined,
  lang: ContentLang,
  next: BlockButton[],
): BlockButton[] => {
  const other = (buttons ?? []).filter((b) => langOf(b) !== lang)
  return lang === 'ru' ? [...next, ...other] : [...other, ...next]
}

/** Write-форма списка: `type` всегда LINK, `order` проставлен явно (§5 — на чтении
 *  сортировка идёт по нему, а не по позиции в массиве), серверные поля сняты
 *  (`id` пересоздаётся на каждом сохранении, §5). */
export const normalizeButtons = (buttons: BlockButton[] | undefined): BlockButton[] =>
  (buttons ?? []).map((b, index) => ({
    type: LINK_BUTTON_TYPE,
    lang: writeLang(b),
    title: trimmed(b.title),
    link: trimmed(b.link),
    slug: null,
    color: null,
    order: index,
  }))

/** Ссылку бек не валидирует вообще (§2) — ни схему, ни формат. Пускаем только https:
 *  `javascript:`/`data:` не должны доехать ни до превью, ни до плеера. */
export const isSafeButtonLink = (link: string | null): boolean => {
  const value = trimmed(link)
  if (!value) return true /* пустая ссылка допустима: публикацию она не блокирует (§7.2) */
  try {
    return new URL(value).protocol === 'https:'
  } catch {
    return false
  }
}
